import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import lottie from "lottie-web";
import Container from "public-components/Container";
import { Store } from "public-components/StoreProvider";
import addPV from "utils/add-pv.js";
import styles from "./HeroSection.module.scss";

import HeroRoundBG from "./hero-round.svg";

const durations = {
  edit: 12,
  design: 21,
  collage: 10.5
};

const HeroSection = ({ data }) => {
  const { dispatch } = React.useContext(Store);
  const [heroBox] = React.useState(React.createRef());
  const [currentAnimation, setCurrentAnimation] = React.useState("");
  const animations = React.useRef({
    edit: null,
    design: null,
    collage: null
  });

  React.useEffect(() => {
    const handlePageScroll = () => {
      dispatch({
        type: "TOGGLE_HEADER_STYLE",
        payload: window.pageYOffset >= heroBox.current.offsetHeight - 200
      });
    };
    handlePageScroll();

    window.addEventListener("scroll", handlePageScroll);

    return () => {
      window.removeEventListener("scroll", handlePageScroll);
    };
  }, [heroBox, dispatch]);

  React.useEffect(() => {
    animations.current = {
      edit: lottie.loadAnimation({
        container: document.getElementById("hero-animation-edit"),
        renderer: "svg",
        loop: false,
        autoplay: false,
        isPaused: true,
        path: `${process.env.ASSET_PREFIX || ""}/animations/edit/data.json`
      }),
      design: lottie.loadAnimation({
        container: document.getElementById("hero-animation-design"),
        renderer: "svg",
        loop: false,
        autoplay: false,
        isPaused: true,
        path: `${process.env.ASSET_PREFIX || ""}/animations/design/data.json`
      }),
      collage: lottie.loadAnimation({
        container: document.getElementById("hero-animation-collage"),
        renderer: "svg",
        loop: false,
        autoplay: false,
        isPaused: true,
        path: `${process.env.ASSET_PREFIX || ""}/animations/collage/data.json`
      })
    };

    Object.keys(animations.current).forEach(i => {
      const index = data.homeHeroLinksList.map(i => i.animation).indexOf(i);
      const nextIndex = index + 1 === data.homeHeroLinksList.length ? 0 : index + 1;
      const next = data.homeHeroLinksList[nextIndex].animation;

      animations.current[i].onComplete = function() {
        if (this._isRepeat) {
          const target = document.querySelector(".animation-active");
          if (target) {
            target.classList.remove("animation-active");
            setTimeout(() => {
              target.classList.add("animation-active");
            }, 1);
          }
          this.goToAndPlay(0);
        } else {
          animations.current[next].goToAndPlay(0);
          setCurrentAnimation(next);
        }
      };
    });

    animations.current[data.homeHeroLinksList[0].animation].play();
    setCurrentAnimation(data.homeHeroLinksList[0].animation);
  }, [setCurrentAnimation, data.homeHeroLinksList]);

  const switchAnimation = name => {
    if (currentAnimation === name) return;
    Object.keys(animations.current).forEach(i => {
      if (i === name) {
        animations.current[name].goToAndPlay(0);
      } else {
        animations.current[i].stop();
      }
    });
    setCurrentAnimation(name);
  };

  const enableAnimationRepeat = name => {
    animations.current[name]._isRepeat = true;
  };

  const disableAnimationRepeat = name => {
    animations.current[name]._isRepeat = false;
  };

  return (
    <div className={styles.hero} ref={heroBox}>
      <HeroRoundBG className={styles.hero__round} />
      <Container>
        <div className={styles.hero__row}>
          <div className={styles.hero__left}>
            <h1 className={styles.hero__heading}>{data.headingH1}</h1>
            {data.homeHeroLinksList.map(i => (
              <a
                key={i.id}
                href={i.href}
                className={classnames(
                  styles.appLink,
                  i.animation === currentAnimation ? styles.active : null
                )}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => {
                  switchAnimation(i.animation);
                  enableAnimationRepeat(i.animation);
                }}
                onMouseLeave={() => disableAnimationRepeat(i.animation)}
                onClick={e =>
                  addPV(e, "trackEvent", {
                    category: "homePage-appEnter",
                    action: "appEnter_click",
                    label: `up|${i.animation}`,
                    path: i.href,
                    openInNewTab: true
                  })
                }
              >
                <h2>{i.text}</h2>
              </a>
            ))}
          </div>

          <div className={styles.hero__right}>
            <div
              id="hero-animation-edit"
              className={classnames(
                styles.animation,
                currentAnimation === "edit" ? styles.active : null
              )}
            ></div>
            <div
              id="hero-animation-design"
              className={classnames(
                styles.animation,
                currentAnimation === "design" ? styles.active : null
              )}
            ></div>
            <div
              id="hero-animation-collage"
              className={classnames(
                styles.animation,
                currentAnimation === "collage" ? styles.active : null
              )}
            ></div>
            <div className={styles.indicator}>
              {data.homeHeroLinksList.map(i => (
                <div
                  key={i.id}
                  className={styles.indicator__item}
                  onClick={() => switchAnimation(i.animation)}
                >
                  <span
                    className={classnames(
                      styles.indicator__bar,
                      i.animation === currentAnimation ? "animation-active" : null
                    )}
                    style={{
                      transitionDuration:
                        i.animation === currentAnimation ? `${durations[i.animation]}s` : 0
                    }}
                  ></span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.mobileHeadings}>
            {data.homeHeroLinksList.map(i => (
              <h2
                key={i.id}
                className={classnames(i.animation === currentAnimation ? styles.active : null)}
              >
                {i.text}
              </h2>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
};

HeroSection.propTypes = {
  data: PropTypes.shape({
    headingH1: PropTypes.string.isRequired,
    homeHeroLinksList: PropTypes.arrayOf(PropTypes.object)
  }).isRequired
};

export default HeroSection;
